import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
// src/pages/warehouse/EinlagernPage.tsx
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useWedgeScanner, useCameraScanner } from '@/hooks/useScanner'
import { classifyScanFrontend } from '@/lib/scan'
import { useScanInfo, sendMove, flushMoves, moveQueue, MoveOfflineUnavailableError, type ScanInfo, type MoveResult, type MoveRejection } from '@/api/warehouse'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RejectionList } from './components/RejectionList'

type Phase =
  | { step: 'warte_palette' }
  | { step: 'palette_info'; paletteNo: string; info: ScanInfo }
  | { step: 'warte_platz'; paletteNo: string }
  // C-I2: confirmInventur traegt weiter, ob der Place-Call, der zu DIESER belegt_warnung
  // fuehrte, bereits mit confirm_inventur:true gesendet wurde (Fall: erst inventur_warnung
  // bestaetigt, Resend liefert dann occupied_warning) — der Misch-Resend muss das erneut
  // mitschicken, sonst faellt der naechste Resend wieder auf inventur_warnung zurueck.
  // Muster: Scan-App src/logic/einlagern-machine.ts (Codex-Fix F5).
  | { step: 'belegt_warnung'; paletteNo: string; placeBarcode: string; message: string; confirmInventur?: boolean }
  // confirmMisch (C-I2, spiegelbildlich): war der Place-Call bereits mit confirm_misch:true
  // unterwegs, muss der Inventur-Resend das WIEDER mitschicken.
  | { step: 'inventur_warnung'; paletteNo: string; placeBarcode?: string; message: string; retry: 'pickup' | 'place'; confirmMisch?: boolean }

export default function EinlagernPage() {
  const [phase, setPhase] = useState<Phase>({ step: 'warte_palette' })
  const [manual, setManual] = useState('')
  const [pendingCount, setPendingCount] = useState(moveQueue.pending().length)
  const [rejectionTick, setRejectionTick] = useState(0)
  const scanInfo = useScanInfo()
  const cam = useCameraScanner((code) => void handleScan(code))
  // RA3-WEB-A: pickup-cancel ist ambig bei Transportfehlern (Server hat evtl. bereits
  // committet, nur die Antwort ging verloren). Ein Retry MUSS denselben idempotency_key
  // wiederverwenden, sonst dedupliziert der Server nicht und fuehrt den Cancel ein
  // zweites Mal aus (koennte einen inzwischen NEUEN Aufnehmer faelschlich freigeben oder
  // faelschlich not_in_transit liefern). Key wird einmalig beim ersten Versuch erzeugt,
  // bleibt bis zu einer DEFINITIVEN Server-Antwort (Erfolg, blocked, harter 4xx) stehen.
  // RA4-WEB-1: der Key ist an die paletteNo GEBUNDEN — startet onCancel fuer eine ANDERE
  // Palette als die zuletzt im Ref gehaltene, wird ein frischer Key erzeugt (sonst koennte
  // ein spaeterer Cancel einer voelligen anderen Palette den stehengebliebenen Key eines
  // fruehreren, ambig gescheiterten Cancels wiederverwenden). Zusaetzlich wird der Ref
  // geleert, sobald die Buchungskette den Cancel-/Platz-Scan-Flow ueber einen ANDEREN Weg
  // verlaesst (z.B. Platz erfolgreich gescannt statt Cancel erneut zu versuchen) — siehe
  // Effect unten.
  const cancelKeyRef = useRef<{ key: string; paletteNo: string } | null>(null)
  // W5-WEB (RA5-WEB-1): Single-Flight fuer pickup-cancel. Waehrend ein Cancel in-flight ist,
  // sind erneute Cancels UND konkurrierende Buchungs-Aktionen (Scan/Pickup/Skip/Misch/Inventur)
  // synchron gesperrt (Ref, nicht nur State — State-Updates sind async). Eine verspaetete
  // Cancel-Antwort wird NUR angewendet, wenn ihr Key noch der aktive Kontext ist (Match-Guard
  // in onCancel) — sonst hat ein anderer Flow (andere Palette, Flow verlassen) uebernommen und
  // die Antwort wird verworfen statt Phase/Key zu ueberschreiben.
  const cancelBusyRef = useRef(false)
  const [cancelBusy, setCancelBusy] = useState(false)

  const refreshPending = () => setPendingCount(moveQueue.pending().length)
  // B2: harte 4xx-Rejects werden aus der Queue entfernt, aber muessen dem Mitarbeiter
  // gemeldet werden (Buchung ist NICHT im Backend gelandet, muss ggf. manuell nachgeholt werden).
  // C-I5: Details (Palette/Aktion/Code/Meldung) landen zusaetzlich im Dead-Letter (RejectionList).
  const handleFlushResult = (r: { sent: number; failed: number; rejected: number; rejections: MoveRejection[] }) => {
    refreshPending()
    if (r.rejected > 0) {
      toast.error(`${r.rejected} gepufferte Buchung(en) vom Server abgelehnt — Details unten`)
      setRejectionTick((t) => t + 1)
    }
  }

  useEffect(() => {
    const t = setInterval(() => { void flushMoves().then(handleFlushResult) }, 10_000)
    const onOnline = () => { void flushMoves().then(handleFlushResult) }
    window.addEventListener('online', onOnline)
    return () => { clearInterval(t); window.removeEventListener('online', onOnline) }
  }, [])

  // RA4-WEB-1: 'warte_palette' bedeutet "kein Palettenvorgang mehr aktiv" — jeder Weg dorthin
  // (Platz erfolgreich gebucht, Ueberspringen, Mischbestaetigung, Cancel-Erfolg, Zurueck-Button)
  // ist ein Verlassen des Cancel-/Platz-Scan-Flows fuer die zuletzt gehaltene Palette. Ein
  // dort noch stehender Cancel-Key (z.B. nach einem ambigen Transportfehler, den der Nutzer
  // nicht erneut versucht hat) darf danach nicht mehr fuer eine kuenftige, andere Palette
  // wiederverwendet werden.
  useEffect(() => {
    if (phase.step === 'warte_palette') cancelKeyRef.current = null
  }, [phase.step])

  const handleMoveResult = useCallback((r: MoveResult | { queued: true }, okMsg: string): boolean => {
    if ('queued' in r) { toast.warning('OFFLINE — Buchung gepuffert (NICHT GEBUCHT)'); refreshPending(); return false }
    if (r.duplicate) { toast.info('Bereits gebucht (Doppel-Scan erkannt)'); return true }
    if (r.blocked) { toast.error(r.message ?? 'Abgelehnt'); return false }
    toast.success(okMsg)
    return true
  }, [])

  const handleScan = useCallback(async (code: string) => {
    // W5-WEB: waehrend ein Cancel in-flight ist, keine konkurrierende Buchung starten —
    // sonst Doppel-Submit-Fenster (Cancel + Place gleichzeitig auf derselben Palette).
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const scan = classifyScanFrontend(code)

    if (phase.step === 'warte_palette' || phase.step === 'palette_info') {
      if (scan.kind !== 'palette') {
        toast.error(scan.kind === 'place' ? 'Das ist ein Platz-Code — zuerst die PALETTE scannen' : `Unbekannter Code: ${code}`)
        return
      }
      // K3: Info zuerst — Aktion ist der bewusste zweite Schritt
      const info = await scanInfo.mutateAsync(code)
      setPhase({ step: 'palette_info', paletteNo: scan.paletteNo, info })
      return
    }

    if (phase.step === 'warte_platz' || phase.step === 'belegt_warnung') {
      if (scan.kind !== 'place') { toast.error('Jetzt den PLATZ-Code am Regal scannen'); return }
      const r = await sendMove('/warehouse/move/place/', {
        palette_no: phase.paletteNo, place_barcode: scan.barcode, confirm_misch: false,
        idempotency_key: newIdempotencyKey(),
      })
      if (!('queued' in r) && r.blocked && r.code === 'occupied_warning') {
        setPhase({ step: 'belegt_warnung', paletteNo: phase.paletteNo, placeBarcode: scan.barcode, message: r.message ?? 'Platz belegt' })
        return
      }
      if (!('queued' in r) && r.blocked && r.code === 'inventur_warnung') {
        setPhase({ step: 'inventur_warnung', paletteNo: phase.paletteNo, placeBarcode: scan.barcode, message: r.message ?? 'Bereich wird gerade gezählt — trotzdem buchen?', retry: 'place' })
        return
      }
      if (handleMoveResult(r, `Palette ${phase.paletteNo} → ${scan.barcode} gebucht (${!('queued' in r) ? r.state : ''})`)) {
        setPhase({ step: 'warte_palette' })
      } else if ('queued' in r) {
        setPhase({ step: 'warte_palette' }) // gepuffert — Kette darf weitergehen, Banner zeigt NICHT GEBUCHT
      }
    }
  }, [phase, scanInfo, handleMoveResult])

  useWedgeScanner((code) => void handleScan(code), true)

  async function onPickup() {
    if (phase.step !== 'palette_info') return
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const r = await sendMove('/warehouse/move/pickup/', { palette_no: phase.paletteNo, idempotency_key: newIdempotencyKey() })
    if (!('queued' in r) && r.blocked && r.code === 'inventur_warnung') {
      setPhase({ step: 'inventur_warnung', paletteNo: phase.paletteNo, message: r.message ?? 'Bereich wird gerade gezählt — trotzdem buchen?', retry: 'pickup' })
      return
    }
    if (handleMoveResult(r, 'Aufgenommen — jetzt einstellen und PLATZ scannen') || 'queued' in r) {
      setPhase({ step: 'warte_platz', paletteNo: phase.paletteNo })
    }
  }
  // paletteNo als Parameter statt aus phase gelesen — wird sowohl aus 'warte_platz' als auch
  // aus dem in_transit-Wiedereinstieg in 'palette_info' aufgerufen (Review Task 16, Finding 1).
  async function onSkip(paletteNo: string) {
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const reason = window.prompt('Grund fuer Ueberspringen (wird Klaerfall beim Leiter)?')
    if (!reason) return
    const r = await sendMove('/warehouse/move/skip/', { palette_no: paletteNo, reason, idempotency_key: newIdempotencyKey() })
    if (handleMoveResult(r, 'Uebersprungen — Klaerfall angelegt') || 'queued' in r) setPhase({ step: 'warte_palette' })
  }
  async function onCancel(paletteNo: string) {
    // RA-WC1: pickup-cancel ist bewusst online-only (allowOffline:false) — ein gepuffertes
    // 'queued' wuerde was_fresh_intake verlieren und der Nutzer laeuft danach zum Start,
    // waehrend die Palette serverseitig weiter in_transit haengt. Bei fehlender Verbindung
    // bleibt die Palette unveraendert im aktuellen Schritt stehen, der Abbruch muss online
    // erneut versucht werden.
    // W5-WEB (RA5-WEB-1): Single-Flight — ein zweiter Klick waehrend der Cancel in-flight
    // ist wird verworfen (synchroner Ref-Check; der Button ist zusaetzlich disabled).
    if (cancelBusyRef.current) return
    if (!cancelKeyRef.current || cancelKeyRef.current.paletteNo !== paletteNo) {
      cancelKeyRef.current = { key: newIdempotencyKey(), paletteNo }
    }
    const idempotencyKey = cancelKeyRef.current.key
    cancelBusyRef.current = true
    setCancelBusy(true)
    try {
      const r = await sendMove('/warehouse/move/pickup-cancel/', { palette_no: paletteNo, idempotency_key: idempotencyKey }, { allowOffline: false })
      // W5-WEB Match-Guard: Antwort nur anwenden, wenn dieser Request noch der aktive
      // Cancel-Kontext ist. Wurde der Flow inzwischen anders verlassen ('warte_palette'-Effect
      // hat den Key geleert) oder gehoert der Key inzwischen einer anderen Palette, dann ist
      // diese (verspaetete) Antwort fremd — verwerfen statt Key/Phase des neuen Flows zu
      // ueberschreiben.
      if (cancelKeyRef.current?.key !== idempotencyKey || cancelKeyRef.current.paletteNo !== paletteNo) return
      // Definitive Server-Antwort erhalten (Erfolg/blocked/duplicate) — der Vorgang ist
      // abgeschlossen, ein etwaiger naechster Cancel-Versuch ist eine NEUE Handlung und
      // bekommt einen frischen Key.
      cancelKeyRef.current = null
      // C-C1 (Pendant B-C1): war_fresh_intake===false → Palette bleibt in_transit (nur der
      // Aufnehmer wurde freigegeben), NICHT "abgebrochen" melden und NICHT zum Start springen —
      // stattdessen im Platz-Scan-Flow bleiben, Rueckstellplatz muss gescannt werden.
      if (!('queued' in r) && !r.blocked && r.was_fresh_intake === false) {
        toast.warning('Palette bleibt in Bewegung — Rückstellplatz scannen')
        setPhase({ step: 'warte_platz', paletteNo })
        return
      }
      if (handleMoveResult(r, 'Aufnahme abgebrochen')) setPhase({ step: 'warte_palette' })
    } catch (e) {
      if (e instanceof MoveOfflineUnavailableError) {
        // RA3-WEB-A: Ausgang unklar — Key bleibt STEHEN, ein Retry muss ihn wiederverwenden.
        toast.error('Abbrechen braucht Verbindung — bitte online erneut versuchen')
        return
      }
      // Harter fachlicher Fehler (kein Transportfehler) — Server hat definitiv nicht committet.
      // Key nur loeschen, wenn er noch zu DIESEM Versuch gehoert (Match-Guard wie oben).
      if (cancelKeyRef.current?.key === idempotencyKey) cancelKeyRef.current = null
      toast.error(e instanceof Error ? e.message : 'Abbrechen fehlgeschlagen')
    } finally {
      cancelBusyRef.current = false
      setCancelBusy(false)
    }
  }
  async function onConfirmMisch() {
    if (phase.step !== 'belegt_warnung') return
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const r = await sendMove('/warehouse/move/place/', {
      palette_no: phase.paletteNo, place_barcode: phase.placeBarcode, confirm_misch: true,
      confirm_inventur: phase.confirmInventur === true,
      idempotency_key: newIdempotencyKey(),
    })
    // C-I2: Warn-Kumulierung — falls der Server TROTZ confirm_misch:true jetzt mit
    // inventur_warnung blockt (bisher noch nicht bestaetigt), confirmMisch:true weitertragen,
    // damit der naechste Resend BEIDE Bestaetigungen mitschickt statt endlos zu pingpongen.
    if (!('queued' in r) && r.blocked && r.code === 'inventur_warnung') {
      setPhase({
        step: 'inventur_warnung', paletteNo: phase.paletteNo, placeBarcode: phase.placeBarcode,
        message: r.message ?? 'Bereich wird gerade gezählt — trotzdem buchen?', retry: 'place', confirmMisch: true,
      })
      return
    }
    if (handleMoveResult(r, 'Als MISCHPALETTE gebucht (alle Positionen ausgepackt/ca.)') || 'queued' in r) {
      setPhase({ step: 'warte_palette' })
    }
  }
  async function onConfirmInventur() {
    if (phase.step !== 'inventur_warnung') return
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    if (phase.retry === 'pickup') {
      const r = await sendMove('/warehouse/move/pickup/', {
        palette_no: phase.paletteNo, idempotency_key: newIdempotencyKey(), confirm_inventur: true,
      })
      if (handleMoveResult(r, 'Aufgenommen — jetzt einstellen und PLATZ scannen') || 'queued' in r) {
        setPhase({ step: 'warte_platz', paletteNo: phase.paletteNo })
      }
      return
    }
    const r = await sendMove('/warehouse/move/place/', {
      palette_no: phase.paletteNo, place_barcode: phase.placeBarcode, confirm_misch: phase.confirmMisch === true,
      idempotency_key: newIdempotencyKey(), confirm_inventur: true,
    })
    // C-I2: Warn-Kumulierung — falls der Server TROTZ confirm_inventur:true jetzt mit
    // occupied_warning blockt (bisher noch nicht bestaetigt), confirmInventur:true
    // weitertragen, damit der Misch-Resend beide Flags mitschickt.
    if (!('queued' in r) && r.blocked && r.code === 'occupied_warning') {
      setPhase({
        step: 'belegt_warnung', paletteNo: phase.paletteNo, placeBarcode: phase.placeBarcode!,
        message: r.message ?? 'Platz belegt', confirmInventur: true,
      })
      return
    }
    if (handleMoveResult(r, `Palette ${phase.paletteNo} → ${phase.placeBarcode} gebucht`) || 'queued' in r) {
      setPhase({ step: 'warte_palette' })
    }
  }
  function onCancelInventurWarnung() {
    if (phase.step !== 'inventur_warnung') return
    // pickup-Warnung: noch nichts gebucht → zurueck zum Start. place-Warnung: Palette ist
    // bereits in_transit → zurueck zu warte_platz (Muster wie belegt_warnung).
    setPhase(phase.retry === 'pickup' ? { step: 'warte_palette' } : { step: 'warte_platz', paletteNo: phase.paletteNo })
  }

  return (
    <div className="p-4 max-w-xl mx-auto space-y-4">
      <h1 className="text-xl font-bold">Einlagern</h1>

      {pendingCount > 0 && (
        <div className="rounded-lg bg-red-600 text-white p-3 font-bold text-center">
          {pendingCount} Buchung(en) NICHT GEBUCHT — werden automatisch nachgesendet
        </div>
      )}
      <RejectionList refreshKey={rejectionTick} />

      {phase.step === 'warte_palette' && (
        <div className="rounded-xl border-2 border-dashed border-line p-8 text-center space-y-3">
          <div className="text-2xl font-bold">Paletten-Zettel scannen</div>
          <div className="text-muted">PDA-Scanner nutzen — oder Kamera / manuelle Eingabe</div>
          {cam.supported
            ? <Button variant="outline" onClick={() => (cam.active ? cam.stop() : void cam.start())}>{cam.active ? 'Kamera stoppen' : 'Kamera-Scan'}</Button>
            : <div className="text-xs text-muted">Kamera-Scan auf diesem Geraet nicht verfuegbar — Scanner oder manuelle Eingabe nutzen</div>}
          <video ref={cam.videoRef} className={cam.active ? 'w-full rounded-lg' : 'hidden'} muted playsInline />
          <div className="flex gap-2 justify-center">
            <Input className="w-56" placeholder="Code eintippen…" value={manual} onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && manual) { void handleScan(manual); setManual('') } }} />
          </div>
        </div>
      )}

      {phase.step === 'palette_info' && (
        phase.info.kind !== 'palette' || !phase.info.label ? (
          // Finding 2: Scan sah wie eine Palette aus, Server kennt das Etikett aber nicht (kind:'unknown')
          // — keine aktive Aufnehmen-Karte zeigen, sondern klaren Fehlzustand mit Rueckweg.
          <div className="rounded-xl border-4 border-red-600 bg-red-50 dark:bg-red-950 p-5 space-y-3 text-center">
            <div className="text-xl font-bold text-red-700">Unbekannter Paletten-Code</div>
            <div className="text-sm">{phase.info.hint ?? 'Zettel pruefen oder Klaerfall melden'}</div>
            <Button variant="outline" onClick={() => setPhase({ step: 'warte_palette' })}>Zurueck</Button>
          </div>
        ) : (
          <div className="rounded-xl border border-line bg-panel p-5 space-y-2">
            <div className="text-lg font-bold">{phase.paletteNo} — Zettel {phase.info.label?.sheetNo}</div>
            <div className="text-2xl font-bold"><GrundartikelLink code={phase.info.artikel?.merchantSku} /></div>
            <div className="text-muted">{phase.info.artikel?.itemName}</div>
            <div>Menge laut Etikett: <b>{phase.info.label?.qty} St.</b></div>
            {phase.info.position && <div>Aktueller Zustand: <b>{phase.info.position.state}</b>{phase.info.place && ` auf ${(phase.info.place as { barcode?: string }).barcode}`}</div>}
            {phase.info.label?.status === 'cancelled' && <div className="text-red-600 font-bold">ETIKETT STORNIERT — nicht verwenden, Klaerfall melden!</div>}
            {phase.info.position?.state === 'in_transit' ? (
              // Finding 1: Palette ist serverseitig schon in_transit (z.B. nach Browser-Reload) — "Aufnehmen"
              // wuerde nur 'already_in_transit' liefern und den User im K2-Limit einsperren. Wiedereinstieg statt Sackgasse.
              <div className="space-y-2">
                <div className="rounded bg-amber-100 text-amber-900 px-2 py-1 text-sm font-semibold">
                  Diese Palette ist bereits in Bewegung — weitermachen oder aufloesen:
                </div>
                <Button className="w-full text-lg py-6" onClick={() => setPhase({ step: 'warte_platz', paletteNo: phase.paletteNo })}>
                  Weiter: Platz jetzt scannen
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" disabled={cancelBusy} onClick={() => void onCancel(phase.paletteNo)}>{cancelBusy ? 'Abbruch läuft…' : 'Aufnahme abbrechen'}</Button>
                  <Button variant="ghost" className="flex-1" onClick={() => void onSkip(phase.paletteNo)}>Ueberspringen (Klaerfall)</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 pt-2">
                <Button className="text-lg py-6 flex-1" onClick={() => void onPickup()} disabled={phase.info.label?.status === 'cancelled'}>
                  Aufnehmen (dann Platz scannen)
                </Button>
                <Button variant="outline" onClick={() => setPhase({ step: 'warte_palette' })}>Zurueck</Button>
              </div>
            )}
            <button className="text-xs underline text-muted" onClick={() => {
              const msg = window.prompt('Was weicht ab? (z.B. "Menge stimmt nicht: Zettel 100, real ~80")')
              if (msg) void sendMove('/warehouse/events/report/', { palette_no: phase.paletteNo, message: msg, idempotency_key: newIdempotencyKey() })
                .then(() => toast.success('Gemeldet — Klaerfall beim Leiter'))
            }}>
              Menge weicht ab / Problem melden (①.13)
            </button>
          </div>
        )
      )}

      {phase.step === 'warte_platz' && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950 p-5 space-y-3 text-center">
          <div className="text-lg font-bold">Palette {phase.paletteNo} in Bewegung</div>
          <div className="text-2xl font-bold">Jetzt PLATZ-Code der Ebene scannen</div>
          <div className="text-sm text-muted">Erst nach dem Einstellen scannen — der Platz-Scan schliesst die Buchung ab (Pflicht)</div>
          {/* Manuelle Eingabe auch hier (Browser-E2E-Befund 2026-07-09): ohne Scanner war
              dieser Schritt eine Sackgasse — nur Klaerfall/Abbrechen moeglich. */}
          <div className="flex gap-2 justify-center">
            <Input className="w-56" placeholder="Platz-Code eintippen…" value={manual} onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && manual) { void handleScan(manual); setManual('') } }} />
          </div>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => void onSkip(phase.paletteNo)}>Ueberspringen (Klaerfall)</Button>
            <Button variant="ghost" disabled={cancelBusy} onClick={() => void onCancel(phase.paletteNo)}>{cancelBusy ? 'Abbruch läuft…' : 'Abbrechen'}</Button>
          </div>
        </div>
      )}

      {phase.step === 'belegt_warnung' && (
        <div className="rounded-xl border-4 border-red-600 bg-red-50 dark:bg-red-950 p-5 space-y-4 text-center">
          <div className="text-2xl font-bold text-red-700">⚠ PLATZ BELEGT</div>
          <div>{phase.message}</div>
          <Button className="w-full text-lg py-6" onClick={() => setPhase({ step: 'warte_platz', paletteNo: phase.paletteNo })}>
            ABBRECHEN — richtigen Platz scannen
          </Button>
          <button className="text-xs underline text-muted" onClick={() => void onConfirmMisch()}>
            Bewusst als Mischpalette auf {phase.placeBarcode} buchen (alle Positionen werden „ausgepackt/ca.")
          </button>
        </div>
      )}

      {phase.step === 'inventur_warnung' && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950 p-5 space-y-4 text-center">
          <div className="text-xl font-bold text-amber-800">⚠ INVENTUR LÄUFT</div>
          <div>{phase.message}</div>
          <Button className="w-full text-lg py-6" onClick={() => void onConfirmInventur()}>
            Trotzdem buchen
          </Button>
          <button className="text-xs underline text-muted" onClick={onCancelInventurWarnung}>
            Abbrechen
          </button>
        </div>
      )}
    </div>
  )
}
